import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Typography, Stack, Paper, CircularProgress, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Collapse, IconButton, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Alert, Divider,
} from '@mui/material'
import CheckCircleOutlinedIcon   from '@mui/icons-material/CheckCircleOutlined'
import CancelOutlinedIcon        from '@mui/icons-material/CancelOutlined'
import WarningAmberOutlinedIcon  from '@mui/icons-material/WarningAmberOutlined'
import RefreshIcon               from '@mui/icons-material/Refresh'
import OpenInNewIcon             from '@mui/icons-material/OpenInNew'
import ExpandMoreIcon            from '@mui/icons-material/ExpandMore'
import ExpandLessIcon            from '@mui/icons-material/ExpandLess'
import {
  getComplianceReport, refreshComplianceReport, setRepoExcluded,
  enableCodeQl, createUnitTestsFixPr, getStatusCheckCandidates, applyStatusChecks,
} from '../../api/client'
import type { RepoComplianceStatus } from '../../api/types'

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function StatusIcon({ ok, flagOnly = false }: { ok: boolean; flagOnly?: boolean }) {
  if (ok)       return <CheckCircleOutlinedIcon sx={{ color: 'success.main', fontSize: 18 }} />
  if (flagOnly) return <WarningAmberOutlinedIcon sx={{ color: 'warning.main', fontSize: 18 }} />
  return <CancelOutlinedIcon sx={{ color: 'error.main', fontSize: 18 }} />
}

function LangChip({ lang }: { lang: string }) {
  const colors: Record<string, string> = {
    'C#': '#9b59b6', JavaScript: '#f1c40f', TypeScript: '#3178c6',
    Go: '#00acd7', Python: '#3776ab', Java: '#e74c3c',
    Ruby: '#cc342d', Swift: '#fa7343', Kotlin: '#7f52ff',
  }
  const bg = colors[lang] ?? '#64748b'
  return (
    <Chip label={lang} size="small"
      sx={{ bgcolor: bg + '22', color: bg, fontWeight: 600, fontSize: 11, height: 20, border: `1px solid ${bg}44` }} />
  )
}

function SummaryChip({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <Chip label={`${count} ${label}`} size="small"
      sx={{ bgcolor: color + '18', color, fontWeight: 700, border: `1px solid ${color}33` }} />
  )
}

// ── Exclude confirmation dialog ───────────────────────────────────────────────

function ExcludeDialog({
  repo, open, onClose, onConfirm,
}: { repo: RepoComplianceStatus | null; open: boolean; onClose: () => void; onConfirm: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Exclude from SDLC Hardening?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <strong>{repo?.fullName}</strong> will be marked as not requiring SDLC hardening pipelines
          and removed from the compliance report.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" color="warning" onClick={onConfirm}>Exclude</Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Status checks confirmation dialog ─────────────────────────────────────────

function StatusChecksDialog({
  repo, contexts, open, onClose, onConfirm,
}: {
  repo: RepoComplianceStatus | null
  contexts: string[]
  open: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Require these status checks?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          These checks are already reporting on <strong>{repo?.defaultBranch}</strong> in{' '}
          <strong>{repo?.fullName}</strong>. Requiring them takes effect immediately for every
          currently-open PR in this repo, not just future ones.
        </DialogContentText>
        <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 3 }}>
          {contexts.map(c => (
            <li key={c}><Typography variant="body2" fontFamily="monospace">{c}</Typography></li>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" onClick={onConfirm}>Require these checks</Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Repo row ──────────────────────────────────────────────────────────────────

function RepoRow({
  repo, onExclude, onInclude, onFixCodeQl, onFixUnitTests, onFixStatusChecks,
  fixingCodeQl, fixingUnitTests, fixingStatusChecks,
}: {
  repo: RepoComplianceStatus
  onExclude: (r: RepoComplianceStatus) => void
  onInclude: (r: RepoComplianceStatus) => void
  onFixCodeQl: (r: RepoComplianceStatus) => void
  onFixUnitTests: (r: RepoComplianceStatus) => void
  onFixStatusChecks: (r: RepoComplianceStatus) => void
  fixingCodeQl: boolean
  fixingUnitTests: boolean
  fixingStatusChecks: boolean
}) {
  return (
    <TableRow hover sx={repo.isExcluded ? { opacity: 0.5 } : {}}>
      <TableCell>
        <Box component="a" href={repo.htmlUrl} target="_blank" rel="noreferrer"
          sx={{ fontWeight: 600, color: 'primary.main', textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {repo.name}
          <OpenInNewIcon sx={{ fontSize: 13, opacity: 0.6 }} />
        </Box>
        <Typography variant="caption" color="text.secondary">{repo.defaultBranch}</Typography>
      </TableCell>

      <TableCell>
        <LangChip lang={repo.primaryLanguage} />
      </TableCell>

      <TableCell align="center">
        <Stack alignItems="center" spacing={0.25}>
          <Tooltip title={repo.hasCodeQl ? 'CodeQL workflow found' : 'No CodeQL workflow detected'}>
            <span><StatusIcon ok={repo.hasCodeQl} /></span>
          </Tooltip>
          {!repo.hasCodeQl && !repo.isExcluded && (
            <Button size="small" variant="text" sx={{ minWidth: 0, fontSize: 11, py: 0 }}
              onClick={() => onFixCodeQl(repo)} disabled={fixingCodeQl}>
              {fixingCodeQl ? <CircularProgress size={12} /> : 'Enable'}
            </Button>
          )}
        </Stack>
      </TableCell>

      <TableCell align="center">
        <Stack alignItems="center" spacing={0.25}>
          <Tooltip title={repo.hasUnitTests ? 'Unit test workflow found' : 'No unit test workflow on PRs detected'}>
            <span><StatusIcon ok={repo.hasUnitTests} /></span>
          </Tooltip>
          {!repo.hasUnitTests && !repo.isExcluded && (
            repo.unitTestsFixPrUrl
              ? <Button size="small" variant="text" sx={{ minWidth: 0, fontSize: 11, py: 0 }}
                  href={repo.unitTestsFixPrUrl} target="_blank">
                  View PR
                </Button>
              : <Button size="small" variant="text" sx={{ minWidth: 0, fontSize: 11, py: 0 }}
                  onClick={() => onFixUnitTests(repo)} disabled={fixingUnitTests}>
                  {fixingUnitTests ? <CircularProgress size={12} /> : 'Create PR'}
                </Button>
          )}
        </Stack>
      </TableCell>

      <TableCell align="center">
        <Stack alignItems="center" spacing={0.25}>
          <Tooltip title={
            repo.hasRequiredStatusChecks
              ? 'Branch protection has required status checks — PRs must pass CI before merge'
              : `No required status checks on "${repo.defaultBranch}"`
          }>
            <span><StatusIcon ok={repo.hasRequiredStatusChecks} flagOnly={!repo.hasRequiredStatusChecks} /></span>
          </Tooltip>
          {!repo.hasRequiredStatusChecks && !repo.isExcluded && (
            <Button size="small" variant="text" sx={{ minWidth: 0, fontSize: 11, py: 0 }}
              onClick={() => onFixStatusChecks(repo)} disabled={fixingStatusChecks}>
              {fixingStatusChecks ? <CircularProgress size={12} /> : 'Configure'}
            </Button>
          )}
        </Stack>
      </TableCell>

      <TableCell>
        {repo.isExcluded
          ? <Button size="small" color="inherit" variant="outlined" onClick={() => onInclude(repo)}>
              Include
            </Button>
          : <Button size="small" color="warning" variant="text" onClick={() => onExclude(repo)}>
              Not required
            </Button>
        }
      </TableCell>
    </TableRow>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function OrgCompliancePage() {
  const qc = useQueryClient()
  const [showExcluded, setShowExcluded] = useState(false)
  const [confirmRepo, setConfirmRepo]   = useState<RepoComplianceStatus | null>(null)
  const [fixingKey,   setFixingKey]     = useState<string | null>(null)
  const [fixError,    setFixError]      = useState<string | null>(null)
  const [statusChecksDialog, setStatusChecksDialog] =
    useState<{ repo: RepoComplianceStatus; contexts: string[] } | null>(null)

  const { data: resp, isLoading, error } = useQuery({
    queryKey: ['compliance'],
    queryFn: getComplianceReport,
    staleTime: 60_000,
  })

  const refreshMut = useMutation({
    mutationFn: refreshComplianceReport,
    onSuccess: (data) => {
      if (data.data) qc.setQueryData(['compliance'], data)
    },
  })

  const excludeMut = useMutation({
    mutationFn: ({ repo, excluded }: { repo: RepoComplianceStatus; excluded: boolean }) =>
      setRepoExcluded(repo.fullName, excluded),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compliance'] }),
  })

  async function handleFixCodeQl(repo: RepoComplianceStatus) {
    setFixError(null)
    setFixingKey(`${repo.fullName}:codeql`)
    try {
      const [owner] = repo.fullName.split('/')
      const res = await enableCodeQl(owner, repo.name)
      if (res.data?.success) {
        qc.invalidateQueries({ queryKey: ['compliance'] })
      } else {
        setFixError(res.data?.message ?? res.error?.message ?? 'Could not enable CodeQL.')
      }
    } finally {
      setFixingKey(null)
    }
  }

  async function handleFixUnitTests(repo: RepoComplianceStatus) {
    setFixError(null)
    setFixingKey(`${repo.fullName}:unit-tests`)
    try {
      const [owner] = repo.fullName.split('/')
      const res = await createUnitTestsFixPr(owner, repo.name)
      if (res.data?.prUrl) {
        qc.invalidateQueries({ queryKey: ['compliance'] })
        window.open(res.data.prUrl, '_blank')
      } else {
        setFixError(res.data?.message ?? res.error?.message ?? 'PR creation failed.')
      }
    } finally {
      setFixingKey(null)
    }
  }

  async function handleFixStatusChecks(repo: RepoComplianceStatus) {
    setFixError(null)
    setFixingKey(`${repo.fullName}:status-checks`)
    try {
      const [owner] = repo.fullName.split('/')
      const res = await getStatusCheckCandidates(owner, repo.name)
      if (res.data && res.data.contexts.length > 0) {
        setStatusChecksDialog({ repo, contexts: res.data.contexts })
      } else {
        setFixError(res.data?.message ?? res.error?.message ?? 'No status checks found to require.')
      }
    } finally {
      setFixingKey(null)
    }
  }

  async function confirmStatusChecks() {
    if (!statusChecksDialog) return
    const { repo, contexts } = statusChecksDialog
    setStatusChecksDialog(null)
    setFixError(null)
    setFixingKey(`${repo.fullName}:status-checks`)
    try {
      const [owner] = repo.fullName.split('/')
      const res = await applyStatusChecks(owner, repo.name, contexts)
      if (res.data?.success) {
        qc.invalidateQueries({ queryKey: ['compliance'] })
      } else {
        setFixError(res.data?.message ?? res.error?.message ?? 'Could not apply required status checks.')
      }
    } finally {
      setFixingKey(null)
    }
  }

  const report = resp?.data
  const active   = report?.repos.filter(r => !r.isExcluded) ?? []
  const excluded = report?.repos.filter(r =>  r.isExcluded) ?? []
  const compliant    = active.filter(r => r.isCompliant).length
  const nonCompliant = active.filter(r => !r.isCompliant).length
  const total        = report?.repos.length ?? 0

  const sorted = [...active].sort((a, b) => {
    if (a.isCompliant !== b.isCompliant) return a.isCompliant ? 1 : -1
    return a.name.localeCompare(b.name)
  })

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Org Compliance</Typography>
          <Typography variant="caption" color="text.secondary">
            SDLC hardening status for all active repositories in{' '}
            <strong>{report?.org ?? '…'}</strong>
            {report && ` · refreshed ${relativeTime(report.lastRefreshed)}`}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {report && (
            <>
              <SummaryChip label="repos"         count={total}        color="#2563eb" />
              <SummaryChip label="compliant"     count={compliant}    color="#16a34a" />
              <SummaryChip label="non-compliant" count={nonCompliant} color="#dc2626" />
              {excluded.length > 0 &&
                <SummaryChip label="excluded" count={excluded.length} color="#64748b" />}
            </>
          )}
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />}
            onClick={() => refreshMut.mutate()}
            disabled={refreshMut.isPending || isLoading}>
            {refreshMut.isPending ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Stack>
      </Stack>

      {fixError && (
        <Alert severity="error" onClose={() => setFixError(null)} sx={{ mb: 2 }}>
          {fixError}
        </Alert>
      )}

      {/* Legend */}
      <Stack direction="row" spacing={3} mb={2} flexWrap="wrap">
        {[
          { icon: <CheckCircleOutlinedIcon sx={{ color: 'success.main', fontSize: 15 }} />, label: 'Compliant' },
          { icon: <CancelOutlinedIcon sx={{ color: 'error.main', fontSize: 15 }} />, label: 'Missing' },
          { icon: <WarningAmberOutlinedIcon sx={{ color: 'warning.main', fontSize: 15 }} />, label: 'Flag only (no auto-fix)' },
        ].map(({ icon, label }) => (
          <Stack key={label} direction="row" alignItems="center" spacing={0.5}>
            {icon}
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Stack>
        ))}
      </Stack>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">Failed to load compliance report.</Alert>
      )}

      {report && (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Repository</TableCell>
                  <TableCell>
                    <Tooltip title="Primary language as reported by GitHub (by byte count). Unknown = GitHub couldn't determine a dominant language — usually infrastructure, YAML, or config-only repos.">
                      <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>Language</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Detected by scanning .github/workflows/ for 'codeql-action/analyze' or 'codeql-action/init', or by GitHub's 'default setup' being configured. 'Enable' turns on default setup directly via API — no workflow file, no PR.">
                      <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>CodeQL</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Detected by scanning .github/workflows/ for a workflow that has a pull_request trigger AND a known test runner (dotnet test, pnpm test, pytest, go test, etc.).">
                      <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>Unit Tests</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Checks both classic branch protection rules and Rulesets for a 'required_status_checks' rule on the default branch. 'Configure' discovers checks already reporting on the default branch and requires them (asks for confirmation first, since it takes effect for every open PR immediately).">
                      <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>Status Checks</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map(repo => (
                  <RepoRow key={repo.fullName} repo={repo}
                    onExclude={r => setConfirmRepo(r)}
                    onInclude={r => excludeMut.mutate({ repo: r, excluded: false })}
                    onFixCodeQl={handleFixCodeQl}
                    onFixUnitTests={handleFixUnitTests}
                    onFixStatusChecks={handleFixStatusChecks}
                    fixingCodeQl={fixingKey === `${repo.fullName}:codeql`}
                    fixingUnitTests={fixingKey === `${repo.fullName}:unit-tests`}
                    fixingStatusChecks={fixingKey === `${repo.fullName}:status-checks`}
                  />
                ))}
                {sorted.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No active repositories found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Excluded repos section */}
      {excluded.length > 0 && (
        <Box mt={3}>
          <Divider />
          <Stack direction="row" alignItems="center" mt={2} mb={1}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ flexGrow: 1 }}>
              Excluded repos ({excluded.length}) — not required for SDLC hardening
            </Typography>
            <IconButton size="small" onClick={() => setShowExcluded(v => !v)}>
              {showExcluded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>

          <Collapse in={showExcluded}>
            <Paper variant="outlined">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Repository</TableCell>
                      <TableCell>Language</TableCell>
                      <TableCell colSpan={3} />
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {excluded.map(repo => (
                      <RepoRow key={repo.fullName} repo={repo}
                        onExclude={() => {}}
                        onInclude={r => excludeMut.mutate({ repo: r, excluded: false })}
                        onFixCodeQl={() => {}}
                        onFixUnitTests={() => {}}
                        onFixStatusChecks={() => {}}
                        fixingCodeQl={false}
                        fixingUnitTests={false}
                        fixingStatusChecks={false}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Collapse>
        </Box>
      )}

      {/* Exclude confirmation */}
      <ExcludeDialog
        repo={confirmRepo}
        open={confirmRepo !== null}
        onClose={() => setConfirmRepo(null)}
        onConfirm={() => {
          if (confirmRepo) excludeMut.mutate({ repo: confirmRepo, excluded: true })
          setConfirmRepo(null)
        }}
      />

      {/* Status checks confirmation */}
      <StatusChecksDialog
        repo={statusChecksDialog?.repo ?? null}
        contexts={statusChecksDialog?.contexts ?? []}
        open={statusChecksDialog !== null}
        onClose={() => setStatusChecksDialog(null)}
        onConfirm={confirmStatusChecks}
      />
    </Box>
  )
}
